import { useState } from "react";
import type { RealmId } from "@chuzi/shared/types";

export type RealmScopeMode = "mine" | "all";

export function realmScopeFromPath(pathname: string): RealmScopeMode {
  return pathname === "/all" ? "all" : "mine";
}

export function realmScopePath(mode: RealmScopeMode): string {
  return mode === "all" ? "/all" : "/my";
}

interface RealmScopeSelectorProps {
  realm: RealmId;
  mode: RealmScopeMode;
  onChange: (mode: RealmScopeMode) => void;
  disabled?: boolean;
}

const MODES: { id: RealmScopeMode; label: string }[] = [
  { id: "mine", label: "MINE" },
  { id: "all", label: "ALL" },
];

export function RealmScopeSelector({
  realm,
  mode,
  onChange,
  disabled,
}: RealmScopeSelectorProps) {
  const [hovered, setHovered] = useState<RealmScopeMode | null>(null);
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120," : "rgba(126,184,255,";
  const bg = isWilds ? "rgba(20,40,20,0.92)" : "rgba(10,16,32,0.92)";
  const activeColor = isWilds ? "#78c878" : "#7eb8ff";
  const idleColor = isWilds ? "rgba(160,220,160,0.45)" : "rgba(126,184,255,0.45)";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 4,
        minWidth: 72,
      }}
    >
      {MODES.map(({ id, label }) => {
        const active = mode === id;
        const hover = hovered === id;
        return (
          <button
            key={id}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onChange(id)}
            onMouseEnter={() => setHovered(id)}
            onMouseLeave={() => setHovered(null)}
            style={{
              border: `1px solid ${accent}${active || hover ? "0.55)" : "0.28)"}`,
              background: active ? `${accent}0.18)` : bg,
              color: active ? activeColor : idleColor,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 2,
              textTransform: "uppercase",
              padding: "8px 10px",
              borderRadius: 999,
              cursor: disabled ? "default" : "pointer",
              opacity: disabled ? 0.4 : 1,
              backdropFilter: "blur(8px)",
              boxShadow: active
                ? `0 0 12px ${accent}0.25)`
                : hover
                  ? `0 0 8px ${accent}0.15)`
                  : "none",
              transition: "border-color 180ms ease, box-shadow 180ms ease, background 180ms ease",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
